import { AnimatePresence, motion } from 'motion/react'
import type { ShopMessageKey } from '../../../i18n'
import {
  CustomerAreaSurfaceCard,
  EmptyStateBlock,
  customerMotionTransition,
} from '../customer-area'
import { ActivitySkeleton, HelpCircleIcon } from './dashboardShared'
import type { DashboardActivityItem } from './dashboardActivityHelpers'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type DashboardActivitySectionProps = {
  reducedMotion: boolean
  supportLoading: boolean
  activityItems: DashboardActivityItem[]
  activityDateFormatter: Intl.DateTimeFormat
  isItalian: boolean
  t: TranslateFn
}

export const DashboardActivitySection = ({
  reducedMotion,
  supportLoading,
  activityItems,
  activityDateFormatter,
  isItalian,
  t,
}: DashboardActivitySectionProps) => (
  <CustomerAreaSurfaceCard
    title={isItalian ? 'Attivita recente' : 'Recent activity'}
    subtitle={
      isItalian
        ? 'Aggiornamenti account e ordini in ordine cronologico.'
        : 'Account and order updates in chronological order.'
    }
  >
    <AnimatePresence mode="wait" initial={false}>
      {supportLoading && activityItems.length === 0 ? (
        <motion.div
          key="activity-loading"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
        >
          <ActivitySkeleton />
        </motion.div>
      ) : null}

      {!supportLoading && activityItems.length === 0 ? (
        <motion.div
          key="activity-empty"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
        >
          <EmptyStateBlock
            icon={<HelpCircleIcon className="h-4 w-4" />}
            title={isItalian ? 'Nessuna attività recente' : 'No recent activity'}
            description={t('customer.dashboard.activity.empty')}
          />
        </motion.div>
      ) : null}

      {activityItems.length > 0 ? (
        <motion.ol
          key="activity-list"
          initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={customerMotionTransition}
          className="space-y-2 border-l border-(--color-border-strong) pl-3"
        >
          {activityItems.map((item) => (
            <motion.li
              key={item.id}
              initial={reducedMotion ? { opacity: 0 } : { opacity: 0, x: -6 }}
              animate={reducedMotion ? { opacity: 1 } : { opacity: 1, x: 0 }}
              transition={customerMotionTransition}
              className="relative"
            >
              <span className="absolute -left-[0.96rem] top-1.5 h-2 w-2 rounded-full bg-(--color-text-muted)" />
              <p className="text-sm font-semibold text-(--color-text-primary)">{item.title}</p>
              <p className="mt-0.5 text-xs text-(--color-text-secondary)">{item.meta}</p>
              {item.timestamp > 0 ? (
                <p className="mt-0.5 text-[11px] text-(--color-text-muted)">{activityDateFormatter.format(new Date(item.timestamp))}</p>
              ) : null}
            </motion.li>
          ))}
        </motion.ol>
      ) : null}
    </AnimatePresence>
  </CustomerAreaSurfaceCard>
)
